import React, { useState, useEffect, useRef } from 'react';
import { useUser } from '../contexts/UserContext';
import { useProcessing } from '../contexts/ProcessingContext';
import { apiEndpoints } from '../config/api';
import CaptionStylePicker from './CaptionStylePicker';
import UpgradeModal from './UpgradeModal';
import ClipResultsSection from './ClipResultsSection';
import './BatchProcessor-engagement.css';
import './enhanced-modal.css';

const ClipGenerator = () => {
  const { user, checkUsageLimits, incrementClipUsage, incrementVideoUsage } = useUser();
  const { processingState, startProcessing, updateProgress, completeProcessing, cancelProcessing } = useProcessing();
  const [videoUrl, setVideoUrl] = useState('');
  const [clips, setClips] = useState([]);
  const [previousClips, setPreviousClips] = useState([]);
  const [previousClipsExpiry, setPreviousClipsExpiry] = useState('');
  const isCurrentSession = React.useRef(false);
  const hasCleared = React.useRef(sessionStorage.getItem('vca_cleared') === 'true');
  const [error, setError] = useState('');
  const [customDuration, setCustomDuration] = useState(15);
  const [captionsEnabled, setCaptionsEnabled] = useState(true);
  const [subtitleLanguage, setSubtitleLanguage] = useState('auto');
  const [translateLanguage, setTranslateLanguage] = useState('');
  const [captionStyle, setCaptionStyle] = useState((user.plan === 'pro' || user.plan === 'business') ? 'highlight' : 'minimal');
  const [captionColour, setCaptionColour] = useState('yellow');
  const [emojiOverlay, setEmojiOverlay] = useState('');
  const [aspectRatio, setAspectRatio] = useState('9:16');
  const [hookEnabled, setHookEnabled] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploadPath, setUploadPath] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [inputMode, setInputMode] = useState('url');
  const [playingVideo, setPlayingVideo] = useState(null); // Track which video is playing
  const [upgradeModal, setUpgradeModal] = useState({ open: false, plan: 'pro', feature: '' });
  const [modalOpen, setModalOpen] = useState(false); // Track modal state
  const [selectedClip, setSelectedClip] = useState(null); // Track selected clip for modal
  
  // Use processing context state instead of local state
  const isLoading = processingState.isProcessing && processingState.type === 'single';
  const [funMessage, setFunMessage] = useState('');
  const [pendingJobId, setPendingJobId] = useState(null);
  const [pendingJobStatus, setPendingJobStatus] = useState(null);
  const activeIntervalsRef = useRef([]);

  useEffect(() => {
    return () => { activeIntervalsRef.current.forEach(id => clearInterval(id)); activeIntervalsRef.current = []; };
  }, []);

  // Check for pending jobs on mount
  React.useEffect(() => {
    const checkPendingJobs = async () => {
      const token = localStorage.getItem('auth_token');
      if (!token) return;
      try {
        const res = await fetch('/api/queue/my-jobs', { headers: { authorization: 'Bearer ' + token } });
        const data = await res.json();
        if (data.pendingJobs && data.pendingJobs.length > 0) {
          const job = data.pendingJobs[0];
          if (job.status === 'queued' || job.status === 'processing') {
            setPendingJobId(job.jobId);
            setPendingJobStatus(job.status);
          } else if (job.status === 'completed' || job.status === 'unknown') {
            // Job done - try to load clips from queue status immediately
            try {
              const statusRes = await fetch('/api/queue/status/' + job.jobId, { headers: { authorization: 'Bearer ' + token } });
              const status = await statusRes.json();
              if (status.status === 'completed' && status.clips && status.clips.length > 0) {
                const processedClips = status.clips.map(clip => ({ ...clip, file: clip.file || clip.videoUrl }));
                setClips(processedClips);
                updateProgress({ status: 'done', step: 'done', progress: 100, message: '🎉 ' + processedClips.length + ' clips ready!' });
              }
            } catch(e) { console.warn('Pending job check failed:', e.message); }
          }
        }
        // Check recent completed jobs from history
        const fromEmailLink = window.location.href.includes('clips');
        if (data.completedJobs && data.completedJobs.length > 0 && (!hasCleared.current || fromEmailLink)) {
          const latest = data.completedJobs[0];
          if (latest.files && latest.files.length > 0) {
            const expiresAt = new Date(latest.expiresAt);
            if (expiresAt > new Date()) {
              const processedClips = latest.files.map((f, i) => ({
                videoUrl: f, file: f, id: i + 1,
                timestamp: latest.clipData && latest.clipData[i] ? latest.clipData[i].timestamp : ('Clip ' + (i + 1)),
                headline: latest.clipData && latest.clipData[i] ? latest.clipData[i].headline : ('Viral Clip #' + (i + 1)),
                engagementScore: latest.clipData && latest.clipData[i] ? Math.round((latest.clipData[i].engagement_score || 0.85) * 100) : (85 + i),
                engagement_score: latest.clipData && latest.clipData[i] ? (latest.clipData[i].engagement_score || 0.85) : 0.85,
                viralPotential: 'High',
                platform: 'TikTok, Instagram Reels',
                segmentNum: i + 1
              }));
              // Always show clips as main — they stay until new ones replace them
              setClips(processedClips);
              updateProgress({ status: 'done', step: 'done', progress: 100, message: '🎉 Your recent clips are ready!' });
            }
          }
        }
      } catch(e) { console.warn('Pending job status fetch failed:', e.message); }
    };
    checkPendingJobs();
  }, []);

  // Poll pending job if exists
  React.useEffect(() => {
    if (!pendingJobId) return;
    let failCount = 0;
    const poll = setInterval(async () => {
      try {
        const res = await fetch('/api/queue/status/' + pendingJobId);
        if (!res.ok) {
          failCount++;
          if (failCount >= 3) {
            // Job no longer exists (server restarted) — reset to fresh
            clearInterval(poll);
            setPendingJobId(null);
            cancelProcessing();
            console.log('Job lost after server restart — resetting');
          }
          return;
        }
        const status = await res.json();
        failCount = 0; // Reset on success
        setPendingJobStatus(status.status);
        if (status.status === 'completed' && status.clips) {
          clearInterval(poll);
          setPendingJobId(null);
          const processedClips = status.clips.map(clip => ({ ...clip, file: clip.file || clip.videoUrl }));
          setClips(processedClips);
          completeProcessing(processedClips, []);
          updateProgress({ status: 'done', step: 'done', progress: 100, message: '🎉 ' + processedClips.length + ' clips created successfully!' });
          incrementClipUsage();
          incrementVideoUsage();
        } else if (status.status === 'error') {
          clearInterval(poll);
          setPendingJobId(null);
          cancelProcessing();
        } else if (status.status === 'unknown' || status.status === 'not_found') {
          failCount++;
          if (failCount >= 3) {
            clearInterval(poll);
            setPendingJobId(null);
            cancelProcessing();
            console.log('Job not found — resetting');
          }
        }
      } catch(e) {
        console.warn('Queue poll failed:', e.message);
        failCount++;
        if (failCount >= 5) {
          clearInterval(poll);
          setPendingJobId(null);
          cancelProcessing();
          console.log('Queue polling failed too many times — resetting');
        }
      }
    }, 3000);
    return () => clearInterval(poll);
  }, [pendingJobId]);
  // Auto-scroll to clips when arriving from email link or page load with clips
  const hasScrolledToClips = React.useRef(false);
  React.useEffect(() => {
    if (hasScrolledToClips.current) return;
    const shouldScroll = sessionStorage.getItem('scrollToClips');
    if (!shouldScroll) return;
    if (clips.length === 0) return;
    hasScrolledToClips.current = true;
    sessionStorage.removeItem('scrollToClips');
    // Use MutationObserver to wait for #clips element to render
    const tryScroll = () => {
      const el = document.getElementById('clips');
      if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'start' }); return true; }
      return false;
    };
    // Try immediately, then retry at intervals
    setTimeout(() => {
      if (!tryScroll()) setTimeout(() => { if (!tryScroll()) setTimeout(tryScroll, 2000); }, 1000);
    }, 500);
  }, [clips]);

  React.useEffect(() => {
    const msgs = ['\u{1F37F} Grab some popcorn, AI is cooking...','\u{1F3AC} Lights, camera, AI action!','\u{1F9E0} Teaching AI to find your best moments...','\u2702\uFE0F Snipping the boring bits out...','\u{1F525} Finding the viral-worthy moments...','\u2615 Perfect time for a quick coffee...','\u{1F3AF} Hunting for engagement gold...','\u{1F680} Launching your content to the moon...','\u{1F48E} Polishing your clips to perfection...','\u{1F355} Go grab a bite, we got this...','\u{1F3AA} The AI magic show is in progress...','\u{1F4AA} Working harder than your gym routine...','\u{1F3B2} Rolling the viral dice in your favour...','\u{1F6F8} Beaming down your clips from the cloud...'];
    if (!isLoading && !pendingJobId) { setFunMessage(''); return; }
    let i = 0; setFunMessage(msgs[0]);
    const interval = setInterval(() => { i = (i + 1) % msgs.length; setFunMessage(msgs[i]); }, 5000);
    return () => clearInterval(interval);
  }, [isLoading, pendingJobId]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const progress = processingState.progress;

  // Restore state when component mounts
  useEffect(() => {
    if (processingState.isProcessing && processingState.type === 'single' && processingState.data) {
      setVideoUrl(processingState.data.videoUrl || '');
      if (processingState.data.customDuration) {
        setCustomDuration(processingState.data.customDuration);
      }
    }
  }, [processingState]);

  // Clear stale cancelled/error states on fresh page load (display only)
  useEffect(() => {
    if (!processingState.isProcessing && progress && 
        (progress.status === 'cancelled' || progress.status === 'error')) {
      updateProgress({ status: 'idle', step: '', progress: 0, message: '' });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const validateUrl = (url) => {
    try {
      new URL(url);
      return true;
    } catch (error) {
      return false;
    }
  };

  const handleCancel = async () => {
    // Cancel on backend if we have a pending job
    if (pendingJobId) {
      try {
        const token = localStorage.getItem('auth_token');
        await fetch('/api/queue/cancel/' + pendingJobId, {
          method: 'POST',
          headers: { 'Authorization': 'Bearer ' + (token || '') }
        });
      } catch(e) { console.warn('Cancel request failed:', e.message); }
      setPendingJobId(null);
    }
    cancelProcessing();
    setClips([]);
    setError('');
    setVideoUrl('');
  };

  // Function to get engagement explanation with detailed summary (matching BatchProcessor)
  const getEngagementExplanation = (clip) => {
    const headline = clip.headline?.toLowerCase() || '';
    const score = Math.round((clip.engagement_score || 0.85) * 100);
    const retentionRate = Math.max(score - 3 + Math.floor(Math.random() * 5), 70);
    
    if (headline.includes('immediate hook') || headline.includes('opening hook') || headline.includes('opening')) {
      return {
        summary: "Critical First Impression",
        details: "Opening hooks capture viewer attention within the first 3 seconds. This segment shows strong narrative setup with " + score + "% predicted engagement.",
        metrics: "Peak retention: " + score + "% | Avg. watch time: 12.3s | Social shares: High"
      };
    } else if (headline.includes('early engagement') || headline.includes('early')) {
      return {
        summary: "Early Content That Builds Interest",
        details: "Early engagement segments establish viewer commitment. AI detected compelling content patterns that keep " + retentionRate + "% of viewers watching.",
        metrics: "Retention rate: " + score + "% | Drop-off prevention: Active | Hook strength: Strong"
      };
    } else if (headline.includes('core content') || headline.includes('core')) {
      return {
        summary: "Core Content Delivery",
        details: "The main content section delivers maximum value. This segment contains the highest information density with " + score + "% viewer engagement.",
        metrics: "Content density: " + score + "% | Value score: High | Rewatch rate: " + retentionRate + "%"
      };
    } else if (headline.includes('key insight') || headline.includes('insight') || headline.includes('revelation')) {
      return {
        summary: "Key Insight Moment",
        details: "Critical information or revelation that drives viewer action. These moments generate " + score + "% engagement and high comment activity.",
        metrics: "Insight score: " + score + "% | Comment rate: Very High | Save rate: " + retentionRate + "%"
      };
    } else if (headline.includes('peak energy') || headline.includes('peak') || headline.includes('energy')) {
      return {
        summary: "Mid-Video Energy Surge",
        details: "Peak energy moments maintain viewer interest through the crucial middle section, preventing the typical 30% mark drop-off.",
        metrics: "Retention boost: " + score + "% | Skip rate: Low | Platform reach: Excellent"
      };
    } else if (headline.includes('climax') || headline.includes('climactic')) {
      return {
        summary: "Emotional Peak Moment",
        details: "Climactic scenes generate maximum emotional response and are 3x more likely to be shared. AI detected " + score + "% engagement potential.",
        metrics: "Engagement spike: " + score + "% | Comments: Very High | Replay rate: " + retentionRate + "%"
      };
    } else if (headline.includes('resolution') || headline.includes('conclusion') || headline.includes('takeaway')) {
      return {
        summary: "Resolution & Key Takeaway",
        details: "The conclusion delivers satisfying closure and a clear call-to-action. Viewers who reach this point have " + retentionRate + "% share likelihood.",
        metrics: "Completion rate: " + score + "% | Share likelihood: High | CTA response: " + retentionRate + "%"
      };
    } else if (headline.includes('final impact') || headline.includes('final') || headline.includes('ending')) {
      return {
        summary: "Memorable Final Impact",
        details: "Closing moments that leave lasting impressions drive " + score + "% higher follow rates and channel subscriptions.",
        metrics: "Impact score: " + score + "% | Follow rate: High | Algorithm boost: " + retentionRate + "%"
      };
    } else if (headline.includes('excitement')) {
      return {
        summary: "High-Energy Content Spike",
        details: "AI-detected excitement peaks show rapid pacing, dynamic visuals, and engaging dialogue that drives " + score + "% viewer engagement.",
        metrics: "Energy level: " + score + "% | Viral potential: High | Algorithm boost: Active"
      };
    } else {
      return {
        summary: "AI-Identified Hotspot",
        details: "Machine learning detected above-average engagement markers including visual interest, audio peaks, and content density at " + score + "% confidence.",
        metrics: "AI confidence: " + score + "% | Engagement score: High | Optimization: Complete"
      };
    }
  };

  // Enhanced video preview modal for starter/free tier
  const handleVideoPreview = (clipIndex, clip) => {
    // Only for starter/free tier users
    if (user.plan !== 'starter' && user.plan !== 'free' && user.plan !== undefined) {
      return;
    }

    setSelectedClip({ ...clip, index: clipIndex });
    setModalOpen(true);
    setPlayingVideo(null); // Reset any playing state
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelectedClip(null);
    setPlayingVideo(null);
    // Remove modal history entry — but only replace state, never go back
    // history.back() can navigate away from the page (email link, direct URL)
    if (window.history.state && window.history.state.modal) {
      window.history.replaceState({ section: 'generator' }, '', window.location.pathname);
    }
  };

  // Intercept device back button when modal is open
  useEffect(() => {
    if (!modalOpen) return;
    // Push a fake history entry so back button closes modal instead of navigating away
    window.history.pushState({ modal: true }, '');
    const handlePop = () => {
      if (modalOpen) {
        setModalOpen(false);
        setSelectedClip(null);
        setPlayingVideo(null);
      }
    };
    window.addEventListener('popstate', handlePop);
    return () => window.removeEventListener('popstate', handlePop);
  }, [modalOpen]);

  const downloadVideo = (videoUrl, filename) => {
    const link = document.createElement('a');
    link.href = videoUrl;
    link.download = filename || 'vlogclip_video.mp4';
    link.click();
  };


  const handleFileUpload = async (file) => {
    if (!file) return;
    if (!file.name.match(/\.(mp4|mov|avi|mkv|webm)$/i)) { setError('Invalid file type. Accepted: MP4, MOV, AVI, MKV, WEBM'); return; }
    const maxMB = user.plan === 'business' ? 5120 : user.plan === 'pro' ? 2048 : 200;
    if (file.size > maxMB * 1024 * 1024) { setError('File too large. Max ' + maxMB + 'MB for your plan.'); return; }
    // Check video duration before uploading (instant, client-side)
    const maxDurationMins = user.plan === 'business' ? 90 : user.plan === 'pro' ? 60 : 30;
    try {
      const fileDuration = await new Promise((resolve) => {
        const video = document.createElement('video');
        video.preload = 'metadata';
        video.onloadedmetadata = () => { window.URL.revokeObjectURL(video.src); resolve(video.duration); };
        video.onerror = () => { window.URL.revokeObjectURL(video.src); resolve(0); };
        video.src = window.URL.createObjectURL(file);
      });
      if (fileDuration > maxDurationMins * 60) {
        const mins = Math.round(fileDuration / 60);
        const upgradeMsg = user.plan === 'free' ? ' Upgrade to Pro for videos up to 60 minutes.' : user.plan === 'pro' ? ' Upgrade to Business for videos up to 90 minutes.' : '';
        setError('Video is ' + mins + ' minutes long. Your ' + (user.plan || 'free') + ' plan supports up to ' + maxDurationMins + ' minutes.' + upgradeMsg);
        return;
      }
    } catch(e) { /* duration check failed, server will catch it */ }
    setUploadedFile(file); setUploadProgress(0); setError('');
    try {
      const formData = new FormData(); formData.append('video', file);
      const xhr = new XMLHttpRequest();
      xhr.upload.onprogress = (e) => { if (e.lengthComputable) setUploadProgress(Math.round((e.loaded / e.total) * 100)); };
      const result = await new Promise((resolve, reject) => {
        xhr.onload = () => { if (xhr.status === 200) resolve(JSON.parse(xhr.responseText)); else { try { reject(new Error(JSON.parse(xhr.responseText).error)); } catch(e2) { reject(new Error('Upload failed')); } } };
        xhr.onerror = () => reject(new Error('Upload failed'));
        xhr.open('POST', '/api/upload');
        xhr.setRequestHeader('Authorization', 'Bearer ' + localStorage.getItem('auth_token'));
        xhr.send(formData);
      });
      setUploadPath(result.uploadPath); setUploadProgress(100);
    } catch (err) { setError('Upload failed: ' + err.message); setUploadedFile(null); setUploadProgress(0); }
  };

  const handleGenerate = async () => {
    if (!uploadPath && !videoUrl.trim()) {
      setError(inputMode === 'upload' ? 'Please upload a video file first' : 'Please enter a video URL');
      return;
    }

    if (!uploadPath && !validateUrl(videoUrl)) {
      setError('Please enter a valid URL');
      return;
    }
    
    // Validate that it's a YouTube URL
    if (!uploadPath && !videoUrl.includes('youtube.com/watch') && !videoUrl.includes('youtu.be/')) {
      setError('Please enter a valid YouTube URL');
      return;
    }

    setIsSubmitting(true); // Immediate visual feedback
    sessionStorage.removeItem('vca_cleared'); hasCleared.current = false;
    // Check usage limits
    const limits = checkUsageLimits();
    if (!limits.canGenerateClips) {
      setIsSubmitting(false);
      setError(`Daily limit reached! You can generate ${limits.limits.clipsPerDay} clips per day on the ${user.plan === 'free' ? 'Free' : user.plan === 'pro' ? 'Pro' : 'Business'} plan. ${user.plan === 'free' ? 'Upgrade to Pro for 150 clips/day!' : user.plan === 'pro' ? 'Upgrade to Business for 450 clips/day!' : 'Limit resets tomorrow.'}`);
      setTimeout(() => { const el = document.getElementById('clip-error-msg'); if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' }); }, 100);
      return;
    }

    if (!limits.canProcessVideo) {
      setIsSubmitting(false);
      setError(`Monthly limit reached! You can process ${limits.limits.videosPerMonth} video${limits.limits.videosPerMonth === 1 ? '' : 's'} per month on the ${user.plan === 'free' ? 'Free' : user.plan === 'pro' ? 'Pro' : 'Business'} plan. ${user.plan === 'free' ? 'Upgrade to Pro for 20 videos/month!' : user.plan === 'pro' ? 'Upgrade to Business for 60 videos/month!' : 'Your credits refresh at the start of your next billing cycle.'}`);
      setTimeout(() => { const el = document.getElementById('clip-error-msg'); if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' }); }, 100);
      return;
    }

    setError('');
    
    // Check for playlist URLs
    if (videoUrl.includes('list=') && !videoUrl.includes('watch?v=')) {
      setIsSubmitting(false);
      alert('⚠️ Playlist URLs are not supported.\n\nPlease paste a direct video URL instead (e.g. youtube.com/watch?v=...)');
      return;
    }
    // Auto-strip playlist/tracking params from video URLs
    if (videoUrl.includes('watch?v=') && (videoUrl.includes('&list=') || videoUrl.includes('&pp=') || videoUrl.includes('&index='))) {
      const cleanUrl = videoUrl.split('&')[0];
      setVideoUrl(cleanUrl);
      // videoUrl reassigned via setVideoUrl above
      alert('ℹ️ Playlist/tracking parameters removed.\n\nProcessing single video: ' + cleanUrl);
    }

    // Check video duration and availability
    try {
      const videoId = videoUrl.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\n?#]+)/)?.[1];
      if (videoId) {
        const infoRes = await fetch('/api/video-duration?videoId=' + videoId);
        if (infoRes.ok) {
          const infoData = await infoRes.json();
          if (!infoData.duration && !infoData.title) {
            setIsSubmitting(false);
            alert('⚠️ This video appears to be unavailable, private, or age-restricted.\n\nPlease check the URL and try a different video.');
            return;
          }
          if (infoData.duration && infoData.duration < 60) {
            setIsSubmitting(false);
            alert('⚠️ This video is only ' + infoData.duration + ' seconds long. Videos under 60 seconds are too short to generate meaningful clips.\n\nPlease use a longer video (2+ minutes recommended).');
            return;
          }
          // Plan-based max duration check
          const planMaxMins = user?.plan === 'business' ? 90 : user?.plan === 'pro' ? 60 : 30;
          const planMaxSecs = planMaxMins * 60;
          if (infoData.duration && infoData.duration > planMaxSecs) {
            setIsSubmitting(false);
            const mins = Math.round(infoData.duration / 60);
            if (user?.plan === 'business') {
              alert('❌ "' + (infoData.title || videoId) + '" is ' + mins + ' mins long. Maximum allowed is 90 minutes on the Business plan. Please use a shorter video.');
            } else {
              const targetPlan = user?.plan === 'pro' ? 'business' : 'pro';
              setUpgradeModal({ open: true, plan: targetPlan, feature: 'Longer Videos (' + mins + ' min)', currentLimit: planMaxMins });
            }
            return;
          }
          // Warn for videos over 20 mins
          if (infoData.duration && infoData.duration > 1200 && infoData.duration <= planMaxSecs) {
            if (!window.confirm('This is a long video (' + Math.round(infoData.duration / 60) + ' minutes). Processing may take additional time. Continue?')) {
              setIsSubmitting(false);
              return;
            }
          } else if (infoData.duration && infoData.duration < 120) {
            // Don't block, just warn - they can still proceed
            if (!window.confirm('This video is only ' + Math.floor(infoData.duration / 60) + ':' + String(Math.floor(infoData.duration % 60)).padStart(2, '0') + ' long. For best results, we recommend videos over 2 minutes. Continue anyway?')) {
              return;
            }
          }
        }
      }
    } catch (durationErr) {
      console.log('Duration check skipped:', durationErr.message);
    }
    
    // Create abort controller for cancellation
    const controller = new AbortController();
    
    // Start processing with global context
    startProcessing('single', {
      videoUrl,
      customDuration: user.plan === 'business' ? customDuration : 15
    }, controller);

    // Scroll to processing panel after a brief delay for it to render
    setTimeout(() => {
      const el = document.getElementById('processing');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 800);

    try {
      // Get progress updates - use more robust approach with error handling
      const progressInterval = setInterval(async () => {
        try {
          // Use centralized API configuration

          const urls = [
            apiEndpoints.progress,
            '/api/progress' // Fallback to proxy
          ];
          
          // Try each URL until one works
          let succeeded = false;
          let progressData = null;
          
          for (const url of urls) {
            try {
              console.log(`Trying to fetch progress from: ${url}`);
              const response = await fetch(url, {
                method: 'GET',
                headers: {
                  'Content-Type': 'application/json',
                  // Add cache control to prevent caching
                  'Cache-Control': 'no-cache, no-store',
                  // Add random parameter to prevent caching
                  'Pragma': 'no-cache'
                },
              });
              
              if (response.ok) {
                progressData = await response.json();
                succeeded = true;
                console.log('Progress fetch succeeded:', progressData);
                break;
              }
            } catch (urlError) {
              console.warn(`Error fetching from ${url}:`, urlError);
            }
          }
          
          if (succeeded && progressData) {
            updateProgress(progressData);
          }
        } catch (error) {
          console.error('Error in progress fetching loop:', error);
        }
      }, 2000);
      activeIntervalsRef.current.push(progressInterval);

      // Generate clips with robust connectivity approach
      const generateUrls = [
        apiEndpoints.generate,
        '/api/generate' // Fallback to proxy
      ];
      
      let response = null;
      let generateError = null;
      
      // Pre-check: verify email status before attempting generation
      const authToken = localStorage.getItem('auth_token');
      try {
        const preCheck = await fetch('/api/generate', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Cache-Control': 'no-cache',
            ...(authToken ? { 'Authorization': 'Bearer ' + authToken } : {}),
          },
          signal: controller.signal,
          body: JSON.stringify({ 
            videoUrl,
            uploadPath: uploadPath || undefined,
            customDuration: user.plan === 'business' ? customDuration : undefined,
            plan: user.plan,
            portraitMode: true,
            aspectRatio: aspectRatio,
            hookEnabled: (user.plan === 'pro' || user.plan === 'business') && hookEnabled ? true : undefined,
            captionsEnabled: captionsEnabled,
            subtitleLanguage: subtitleLanguage !== 'auto' ? subtitleLanguage : undefined,
            translateLanguage: translateLanguage || undefined,
            captionStyle: (user.plan === 'pro' || user.plan === 'business') ? captionStyle : undefined,
            captionColour: (user.plan === 'pro' || user.plan === 'business') ? captionColour : undefined,
            emojiOverlay: (user.plan === 'pro' || user.plan === 'business') && emojiOverlay ? emojiOverlay : undefined,
            userId: user.userId,
            deviceFingerprint: user.deviceFingerprint,
            deviceId: user.deviceId
          }),
        });
        
        // Handle 403 verification block immediately
        if (preCheck.status === 403) {
          const errData = await preCheck.json().catch(() => ({}));
          const msg = errData.error || 'Access denied';
          if (msg.includes('verify')) {
            alert('📧 Email Verification Required\n\n' + msg + '\n\nPlease check your inbox and click the verification link.');
          } else {
            alert('⚠️ Access Denied\n\n' + msg);
          }
          if (progressInterval) clearInterval(progressInterval);
          setError('');
          setClips([]);
          setIsSubmitting(false);
          cancelProcessing();
          if (controller) controller.abort();
          return;
        }
        
        if (preCheck.ok) {
          response = preCheck;
          console.log('Generation request succeeded!');
        }
      } catch (preCheckErr) {
        console.warn('Pre-check error:', preCheckErr);
      }
      
      if (!response || !response.ok) {
        console.warn('Generate request failed with status:', response?.status);
        generateError = new Error('Generate request failed');
      }
      
      // If fetch failed but processing may still be running in background
      if (!response || !response.ok) {
        console.log('Generate request failed or timed out - checking if processing is still running...');
        let retryCount = 0;
        const maxRetries = 150;
        const waitForCompletion = setInterval(async () => {
          retryCount++;
          try {
            const checkResponse = await fetch('/api/progress');
            const checkProgress = await checkResponse.json();
            console.log('Background check #' + retryCount + ':', checkProgress.status, checkProgress.progress + '%');
            if (checkProgress.status === 'completed' || checkProgress.status === 'done') {
              clearInterval(waitForCompletion);
              clearInterval(progressInterval);
              try {
                const clipsResponse = await fetch('/api/last-clips');
                const clipsData = await clipsResponse.json();
                if (clipsData.clips && clipsData.clips.length > 0) {
                  const processedClips = clipsData.clips.map(clip => ({
                    ...clip,
                    file: clip.file || clip.videoUrl
                  }));
                  setClips(processedClips);
                  setError('');
                  incrementClipUsage();
                  incrementVideoUsage();
                  completeProcessing(processedClips, []);
                  setIsSubmitting(false);
                  return;
                }
              } catch (e) { console.log('Failed to get clips:', e); }
            }
            if (checkProgress.status === 'error' || checkProgress.status === 'failed' || retryCount >= maxRetries) {
              clearInterval(waitForCompletion);
              clearInterval(progressInterval);
              setError('Video processing failed. Please try again.');
              updateProgress({ status: 'error', step: 'failed', progress: 0, message: 'Processing failed' });
            }
          } catch (e) {
            if (retryCount >= maxRetries) {
              clearInterval(waitForCompletion);
              clearInterval(progressInterval);
              setError('Lost connection to server. Please refresh and try again.');
            }
          }
        }, 2000);
        activeIntervalsRef.current.push(waitForCompletion);
        return;
      }
      
      const data = await response.json();

      // QUEUE MODE: job was queued, poll for completion
      if (data.queued && data.jobId) {
        clearInterval(progressInterval);
        updateProgress({ status: 'processing', step: 'queued', progress: 5, message: data.message || 'Your clips are in the queue...' });
        // Poll queue status
        const pollQueue = setInterval(async () => {
          try {
            const statusRes = await fetch('/api/queue/status/' + data.jobId);
            const status = await statusRes.json();
            if (status.status === 'processing') {
              updateProgress({ status: 'processing', step: 'processing', progress: Math.min(50 + (Date.now() % 40), 90), message: 'Processing your clips...' });
            } else if (status.status === 'completed' && status.clips) {
              clearInterval(pollQueue);
              const processedClips = status.clips.map(clip => ({ ...clip, file: clip.file || clip.videoUrl }));
              setClips(processedClips);
              incrementClipUsage();
              incrementVideoUsage();
              completeProcessing(processedClips, []);
              setIsSubmitting(false);
              updateProgress({ status: 'done', step: 'done', progress: 100, message: '🎉 ' + processedClips.length + ' clips created successfully!' });
            } else if (status.status === 'error') {
              clearInterval(pollQueue);
              setError(status.error || 'Processing failed');
              setIsSubmitting(false);
            }
          } catch(e) { console.warn('Background poll failed:', e.message); }
        }, 3000);
        activeIntervalsRef.current.push(pollQueue);
        return;
      }

      const clips = data.clips.map(clip => ({
        ...clip,
        file: clip.file || clip.videoUrl
      }));
      setClips(clips);
      
      // Update usage tracking
      incrementClipUsage();
      incrementVideoUsage();
      // Stop progress updates
      clearInterval(progressInterval);
      
      // Complete processing with global context
      completeProcessing(clips, []);
      setIsSubmitting(false);
    } catch (error) {
      setError(error.message);
      updateProgress({
        status: 'error',
        step: 'failed',
        progress: 0,
        message: 'Processing failed'
      });
    }
  };

  // EMERGENCY FIXED: Handle download functionality with bulletproof paths
  const handleDownload = async (fileUrl, filename) => {
    try {
      console.log('💾 EMERGENCY DOWNLOAD:', { fileUrl, filename });
      
      // Extract filename from URL with multiple fallbacks
      let fileName = fileUrl.split('/').pop();
      if (!fileName || !fileName.includes('.mp4')) {
        fileName = fileUrl.replace('/uploads/', '');
      }
      
      console.log('💾 Using filename:', fileName);
      
      // Create multiple download attempts
      const downloadUrls = [
        `/api/download/${fileName}`,
        `/api/download/${fileName}`,
        `${fileUrl}`,
        fileUrl
      ];
      
      // BULLETPROOF DOWNLOAD: Use fetch + blob for guaranteed success
      const downloadWithFetch = async (url) => {
        try {
          console.log('💾 FETCH DOWNLOAD:', url);
          const response = await fetch(url);
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          
          const blob = await response.blob();
          const downloadUrl = window.URL.createObjectURL(blob);
          
          const link = document.createElement('a');
          link.href = downloadUrl;
          link.download = filename || 'clip.mp4';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          
          // Clean up
          setTimeout(() => window.URL.revokeObjectURL(downloadUrl), 1000);
          console.log('✅ FETCH DOWNLOAD SUCCESS');
          return true;
        } catch (error) {
          console.warn('⚠️ FETCH DOWNLOAD FAILED:', url, error);
          return false;
        }
      };
      
      // Try fetch download first, then fallback to direct links
      for (const url of downloadUrls) {
        const success = await downloadWithFetch(url);
        if (success) return;
        
        // Fallback to direct link
        try {
          console.log('💾 DIRECT LINK FALLBACK:', url);
          const link = document.createElement('a');
          link.href = url;
          link.download = filename || 'clip.mp4';
          link.target = '_blank';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          console.log('✅ DIRECT LINK SUCCESS');
          return;
        } catch (urlError) {
          console.warn('⚠️ DIRECT LINK FAILED:', url, urlError);
        }
      }
      
      throw new Error('All download URLs failed');
    } catch (error) {
      console.error('❌ EMERGENCY: Download completely failed:', error);
      alert(`Download failed: ${error.message}. Please try again or contact support.`);
    }
  };

  // Handle share functionality
  const handleShare = async (clip) => {
    try {
      const shareData = {
        title: clip.headline,
        text: `Check out this viral clip: ${clip.headline}`,
        url: window.location.href
      };

      if (navigator.share) {
        // Use native share API if available
        await navigator.share(shareData);
      } else {
        // Fallback: copy to clipboard
        const shareText = `${clip.headline}\n\nTikTok: ${clip.captions.tiktok}\nTwitter: ${clip.captions.twitter}\nLinkedIn: ${clip.captions.linkedin}\nInstagram: ${clip.captions.instagram}`;
        await navigator.clipboard.writeText(shareText);
        alert('Clip details copied to clipboard!');
      }
    } catch (error) {
      console.error('Share failed:', error);
      // Fallback: just copy the headline
      try {
        await navigator.clipboard.writeText(clip.headline);
        alert('Clip title copied to clipboard!');
      } catch (clipError) {
        alert('Share failed. Please try again.');
      }
    }
  };

  // Handle save functionality
  const handleSave = (clip) => {
    try {
      // Save to localStorage
      const savedClips = JSON.parse(localStorage.getItem('savedClips') || '[]');
      const clipToSave = {
        ...clip,
        savedAt: new Date().toISOString(),
        id: Date.now()
      };
      
      savedClips.push(clipToSave);
      localStorage.setItem('savedClips', JSON.stringify(savedClips));
      
      alert('Clip saved successfully!');
    } catch (error) {
      console.error('Save failed:', error);
      alert('Save failed. Please try again.');
    }
  };

  // Handle refresh/reset functionality
  const handleRefresh = () => {
    setVideoUrl('');
    setClips([]);
    setError('');
    setIsSubmitting(false);
    hasCleared.current = true;
    sessionStorage.setItem('vca_cleared', 'true');
    cancelProcessing(); // This will reset processing state
    updateProgress({ status: 'idle', step: '', progress: 0, message: '' });
    console.log('🔄 Interface refreshed - ready for new YouTube URL');
  };

  // Hide single video input for Pro and Business plans
  if (user.plan === 'pro' || user.plan === 'business') {
    return null; // They should use batch processing instead
  }

  return (
    <div className="clip-generator">
      {pendingJobId && pendingJobStatus !== 'completed' && (
        <div style={{
          margin:'0 0 20px 0',
          padding:'18px 24px',
          background:'linear-gradient(135deg, rgba(124,58,237,0.4) 0%, rgba(59,130,246,0.4) 50%, rgba(236,72,153,0.3) 100%)',
          borderRadius:'14px',
          border:'2px solid rgba(167,139,250,0.6)',
          textAlign:'center',
          boxShadow:'0 0 30px rgba(124,58,237,0.5), 0 0 60px rgba(124,58,237,0.2)',
          animation:'pendingPulse 2s ease-in-out infinite',
          position:'relative',
          overflow:'hidden'
        }}>
          <div style={{position:'absolute',inset:0,background:'linear-gradient(135deg,rgba(124,58,237,0.1),rgba(59,130,246,0.1))',animation:'gradientPulse 3s ease infinite'}}></div>
          <div style={{position:'relative',zIndex:1}}>
            <div style={{color:'#e2e8f0',fontSize:'16px',fontWeight:'800',letterSpacing:'0.3px',marginBottom:'6px'}}>
              {pendingJobStatus === 'processing' ? '⚙️ Your clips are being processed!' : '⏳ Your clips are queued!'}
            </div>
            <div style={{color:'#c4b5fd',fontSize:'13px',fontWeight:'600'}}>
              We'll notify you by email when ready — or wait here and they'll appear automatically ✨
            </div>
            {funMessage && (
              <div style={{marginTop:'12px',padding:'10px 16px',background:'rgba(0,0,0,0.2)',borderRadius:'10px',color:'#f1f5f9',fontSize:'14px',fontWeight:'700'}}>
                {funMessage}
              </div>
            )}
          </div>
        </div>
      )}
        <div style={{padding:'0 0 8px 0'}}><button className="dash-back-btn" onClick={() => { window.history.pushState({section:'dashboard'}, '', '/'); window.dispatchEvent(new PopStateEvent('popstate', {state:{section:'dashboard'}})); }}>🏠 ← Dashboard</button></div>
      {/* Input Section */}
      <div className="input-section">
        <div className="input-container">
{/* Mode Tabs */}
          <div style={{display:'flex',gap:'0',marginBottom:'14px',borderRadius:'10px',overflow:'hidden',border:'1px solid rgba(124,58,237,0.3)'}}>
            <button onClick={() => {setInputMode('url');setUploadedFile(null);setUploadPath('');setUploadProgress(0);}} style={{flex:1,padding:'12px',background:inputMode==='url'?'rgba(124,58,237,0.3)':'transparent',color:inputMode==='url'?'#c4b5fd':'#64748b',border:'none',cursor:'pointer',fontWeight:'700',fontSize:'14px',transition:'all 0.2s'}}>🔗 YouTube URL</button>
            <button onClick={() => {
              if (user.plan === 'free' || user.plan === 'starter' || !user.plan) {
                setUpgradeModal({ open: true, plan: 'pro', feature: 'Video Upload' });
                return;
              }
              setInputMode('upload');setVideoUrl('');
            }} style={{flex:1,padding:'12px',background:inputMode==='upload'?'rgba(124,58,237,0.3)':'transparent',color:inputMode==='upload'?'#c4b5fd':'#64748b',border:'none',cursor:'pointer',fontWeight:'700',fontSize:'14px',transition:'all 0.2s'}}>
              📁 Upload Video {(user.plan === 'free' || user.plan === 'starter' || !user.plan) ? '🔒' : ''}
            </button>
          </div>
          {inputMode === 'url' ? (
          <div className="input-wrapper">
            <div className="input-icon">🎥</div>
            <div style={{position:'relative',width:'100%'}}>
              <input type="text" value={videoUrl} onChange={(e) => setVideoUrl(e.target.value)} placeholder="Paste your YouTube URL here..." className="video-input" onKeyPress={(e) => e.key === 'Enter' && handleGenerate()} style={{paddingRight:'40px'}} />
              {videoUrl && (
                <button onClick={() => { setVideoUrl(''); setClips([]); setError(''); }} style={{position:'absolute',right:'12px',top:'50%',transform:'translateY(-50%)',background:'rgba(255,255,255,0.1)',border:'none',color:'#94a3b8',fontSize:'16px',cursor:'pointer',borderRadius:'50%',width:'28px',height:'28px',display:'flex',alignItems:'center',justifyContent:'center',fontWeight:'700',transition:'all 0.2s'}} title="Clear URL">✕</button>
              )}
            </div>
            <div style={{display:'flex',gap:'12px',alignItems:'center',justifyContent:'center',marginTop:'10px',flexWrap:'wrap'}}>
              <span style={{color:'#a78bfa',fontSize:'13px',fontWeight:'700'}}>🎬 {user.plan === 'business' ? '8' : user.plan === 'pro' ? '5' : '3'} viral clips generated per video</span>
              <span style={{color:'#64748b',fontSize:'12px'}}>•</span>
              <span style={{color:'#94a3b8',fontSize:'12px',fontWeight:'600'}}>15-second clips optimised for TikTok, Reels & Shorts</span>
            </div>
            <p style={{color:'#f1f5f9',fontSize:'12px',marginTop:'6px',textAlign:'center',lineHeight:'1.4',fontWeight:'600',opacity:0.8}}>⚠️ Some videos may not process if they are restricted, private, age-gated, or part of a playlist. Use direct video URLs for best results.</p>
          </div>
          ) : (
          <div onDragOver={(e)=>{e.preventDefault();e.currentTarget.style.borderColor='#7c3aed';}} onDragLeave={(e)=>{e.currentTarget.style.borderColor='rgba(124,58,237,0.3)';}} onDrop={(e)=>{e.preventDefault();e.currentTarget.style.borderColor='rgba(124,58,237,0.3)';handleFileUpload(e.dataTransfer.files[0]);}} onClick={()=>document.getElementById('fileInput').click()} style={{border:'2px dashed rgba(124,58,237,0.3)',borderRadius:'14px',padding:'30px 20px',textAlign:'center',cursor:'pointer',background:'rgba(124,58,237,0.05)',transition:'all 0.3s',minHeight:'120px',display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',gap:'8px',marginBottom:'12px'}}>
            <input id="fileInput" type="file" accept=".mp4,.mov,.avi,.mkv,.webm" style={{display:'none'}} onChange={(e)=>handleFileUpload(e.target.files[0])} />
            {uploadedFile ? (<>
              <span style={{fontSize:'28px'}}>✅</span>
              <span style={{color:'#c4b5fd',fontSize:'14px',fontWeight:'700'}}>{uploadedFile.name}</span>
              <span style={{color:'#64748b',fontSize:'12px'}}>{Math.round(uploadedFile.size/1024/1024)}MB</span>
              {uploadProgress < 100 && (<div style={{width:'80%',height:'6px',background:'rgba(124,58,237,0.1)',borderRadius:'3px',overflow:'hidden',marginTop:'4px'}}><div style={{width:uploadProgress+'%',height:'100%',background:'linear-gradient(90deg,#7c3aed,#06b6d4)',borderRadius:'3px',transition:'width 0.3s'}}></div></div>)}
              {uploadProgress === 100 && <span style={{color:'#22c55e',fontSize:'12px',fontWeight:'600'}}>Ready to generate!</span>}
              <button onClick={(e)=>{e.stopPropagation();setUploadedFile(null);setUploadPath('');setUploadProgress(0);}} style={{marginTop:'4px',background:'rgba(239,68,68,0.15)',border:'1px solid rgba(239,68,68,0.3)',color:'#f87171',padding:'6px 14px',borderRadius:'8px',cursor:'pointer',fontSize:'12px',fontWeight:'600'}}>Remove</button>
            </>) : (<>
              <span style={{fontSize:'36px'}}>📁</span>
              <span style={{color:'#c4b5fd',fontSize:'15px',fontWeight:'700'}}>Drop your video here or click to browse</span>
              <span style={{color:'#64748b',fontSize:'12px'}}>MP4, MOV, AVI, MKV, WEBM — Max {user.plan === 'business' ? '5GB' : user.plan === 'pro' ? '2GB' : '200MB'}</span>
            </>)}
          </div>
          )}
          <div className="aspect-ratio-picker" style={{display:'flex',alignItems:'center',justifyContent:'center',margin:'16px 0',gap:'10px',padding:'12px 20px',background:'rgba(124,58,237,0.1)',borderRadius:'12px',border:'1px solid rgba(124,58,237,0.3)',opacity:(isLoading||isSubmitting)?0.5:1,pointerEvents:(isLoading||isSubmitting)?'none':'auto',flexWrap:'wrap'}}>
            <span style={{display:'flex',alignItems:'center',gap:'8px'}}><span style={{fontSize:'18px'}}>📐</span><span style={{color:'#f1f5f9',fontSize:'14px',fontWeight:'700',whiteSpace:'nowrap'}}>Aspect Ratio</span></span>
            <div style={{display:'flex',gap:'6px'}}>
              <button onClick={() => { if (!isLoading && !isSubmitting) setAspectRatio('9:16'); }} style={{padding:'8px 14px',borderRadius:'10px',border: aspectRatio === '9:16' ? '2px solid #a78bfa' : '1px solid rgba(124,58,237,0.3)',background: aspectRatio === '9:16' ? 'rgba(124,58,237,0.3)' : 'transparent',color: aspectRatio === '9:16' ? '#c4b5fd' : '#64748b',cursor:'pointer',fontWeight:'700',fontSize:'13px',transition:'all 0.2s',whiteSpace:'nowrap'}}>9:16</button>
              <button onClick={() => { if (!isLoading && !isSubmitting) setAspectRatio('4:5'); }} style={{padding:'8px 14px',borderRadius:'10px',border: aspectRatio === '4:5' ? '2px solid #a78bfa' : '1px solid rgba(124,58,237,0.3)',background: aspectRatio === '4:5' ? 'rgba(124,58,237,0.3)' : 'transparent',color: aspectRatio === '4:5' ? '#c4b5fd' : '#64748b',cursor:'pointer',fontWeight:'700',fontSize:'13px',transition:'all 0.2s',whiteSpace:'nowrap'}}>4:5</button>
              <button onClick={() => { if (!isLoading && !isSubmitting) setAspectRatio('1:1'); }} style={{padding:'8px 14px',borderRadius:'10px',border: aspectRatio === '1:1' ? '2px solid #a78bfa' : '1px solid rgba(124,58,237,0.3)',background: aspectRatio === '1:1' ? 'rgba(124,58,237,0.3)' : 'transparent',color: aspectRatio === '1:1' ? '#c4b5fd' : '#64748b',cursor:'pointer',fontWeight:'700',fontSize:'13px',transition:'all 0.2s',whiteSpace:'nowrap'}}>1:1</button>
            </div>
            <span style={{color:'#64748b',fontSize:'11px',fontWeight:'600',whiteSpace:'nowrap'}}>{aspectRatio === '9:16' ? 'TikTok, Reels, Shorts' : aspectRatio === '4:5' ? 'Instagram Feed, Facebook' : 'Instagram, LinkedIn, X'}</span>
          </div>
          <div style={{display:'flex',alignItems:'center',justifyContent:'center',margin:'16px 0',gap:'14px',padding:'12px 24px',background: hookEnabled ? 'rgba(236,72,153,0.15)' : 'rgba(124,58,237,0.1)',borderRadius:'12px',border: hookEnabled ? '1px solid rgba(236,72,153,0.4)' : '1px solid rgba(124,58,237,0.3)',opacity:(isLoading||isSubmitting)?0.5:1,pointerEvents:(isLoading||isSubmitting)?'none':'auto'}}>
            <span style={{fontSize:'20px'}}>🪝</span>
            <span style={{color:'#f1f5f9',fontSize:'15px',fontWeight:'700',letterSpacing:'0.3px'}}>AI Hook</span>
            <div
              onClick={() => {
                if (isLoading || isSubmitting) return;
                if (user.plan === 'free' || user.plan === 'starter' || !user.plan) {
                  setUpgradeModal({ open: true, plan: 'pro', feature: 'AI Hook' });
                  return;
                }
                setHookEnabled(prev => !prev);
              }}
              style={{
                width:'56px',height:'30px',borderRadius:'15px',
                backgroundColor: hookEnabled ? '#ec4899' : '#334155',
                cursor:'pointer',position:'relative',transition:'all 0.3s ease',
                border: hookEnabled ? '2px solid #f472b6' : '2px solid #475569',
                boxShadow: hookEnabled ? '0 0 12px rgba(236,72,153,0.4)' : 'none'
              }}
            >
              <div style={{
                width:'22px',height:'22px',borderRadius:'50%',backgroundColor:'white',
                position:'absolute',top:'2px',left: hookEnabled ? '30px' : '2px',
                transition:'left 0.3s ease',boxShadow:'0 2px 4px rgba(0,0,0,0.3)'
              }}></div>
            </div>
            <span style={{color: hookEnabled ? '#f472b6' : '#64748b',fontSize:'14px',fontWeight:'700',minWidth:'30px'}}>
              {hookEnabled ? 'ON' : 'OFF'}
            </span>
            {(user.plan === 'free' || user.plan === 'starter' || !user.plan) && <span style={{fontSize:'14px'}}>🔒</span>}
          </div>
          {hookEnabled && (user.plan === 'pro' || user.plan === 'business') && (
            <div style={{margin:'-8px 0 12px 0',textAlign:'center'}}>
              <span style={{color:'#f472b6',fontSize:'12px',fontWeight:'600'}}>AI generates an attention-grabbing hook overlay on the first 3 seconds of each clip</span>
            </div>
          )}
          <div style={{display:'flex',alignItems:'center',justifyContent:'center',margin:'16px 0',gap:'14px',padding:'12px 24px',background:'rgba(124,58,237,0.1)',borderRadius:'12px',border:'1px solid rgba(124,58,237,0.3)',opacity:(isLoading||isSubmitting)?0.5:1,pointerEvents:(isLoading||isSubmitting)?'none':'auto'}}>
            <span style={{fontSize:'20px'}}>📝</span>
            <span style={{color:'#f1f5f9',fontSize:'15px',fontWeight:'700',letterSpacing:'0.3px'}}>AI Captions</span>
            <div 
              onClick={() => { if (!isLoading && !isSubmitting) setCaptionsEnabled(prev => !prev); }}
              style={{
                width:'56px',height:'30px',borderRadius:'15px',
                backgroundColor: captionsEnabled ? '#7c3aed' : '#334155',
                cursor:'pointer',position:'relative',transition:'all 0.3s ease',
                border: captionsEnabled ? '2px solid #a78bfa' : '2px solid #475569',
                boxShadow: captionsEnabled ? '0 0 12px rgba(124,58,237,0.4)' : 'none'
              }}
            >
              <div style={{
                width:'22px',height:'22px',borderRadius:'50%',backgroundColor:'white',
                position:'absolute',top:'2px',left: captionsEnabled ? '30px' : '2px',
                transition:'left 0.3s ease',boxShadow:'0 2px 4px rgba(0,0,0,0.3)'
              }}></div>
            </div>
            <span style={{color: captionsEnabled ? '#a78bfa' : '#64748b',fontSize:'14px',fontWeight:'700',minWidth:'30px'}}>
              {captionsEnabled ? 'ON' : 'OFF'}
            </span>
          </div>
          {captionsEnabled && (
            <div style={{margin:'0 0 16px 0',padding:'12px 16px',background:'rgba(124,58,237,0.08)',borderRadius:'12px',border:'1px solid rgba(124,58,237,0.2)',opacity:(isLoading||isSubmitting)?0.5:1,pointerEvents:(isLoading||isSubmitting)?'none':'auto'}}>
              <div style={{display:'flex',alignItems:'center',gap:'10px',flexWrap:'wrap',justifyContent:'center'}}>
                <span style={{fontSize:'16px'}}>🌍</span>
                <span style={{color:'#c4b5fd',fontSize:'13px',fontWeight:'700'}}>Subtitle Language</span>
                <select
                  value={subtitleLanguage}
                  onChange={(e) => setSubtitleLanguage(e.target.value)}
                  style={{
                    background:'rgba(30,41,59,0.9)',
                    color:'#f1f5f9',
                    border:'1px solid rgba(124,58,237,0.4)',
                    borderRadius:'8px',
                    padding:'6px 12px',
                    fontSize:'13px',
                    fontWeight:'600',
                    cursor:'pointer',
                    outline:'none',
                    flex:'1',
                    minWidth:'140px',
                    maxWidth:'200px'
                  }}
                >
                  <option value="auto">🔍 Auto-detect</option>
                  <option value="en">🇬🇧 English</option>
                  <option value="es">🇪🇸 Spanish</option>
                  <option value="fr">🇫🇷 French</option>
                  <option value="de">🇩🇪 German</option>
                  <option value="pt">🇵🇹 Portuguese</option>
                  <option value="it">🇮🇹 Italian</option>
                  <option value="nl">🇳🇱 Dutch</option>
                  <option value="ar">🇸🇦 Arabic</option>
                  <option value="hi">🇮🇳 Hindi</option>
                  <option value="ja">🇯🇵 Japanese</option>
                  <option value="ko">🇰🇷 Korean</option>
                  <option value="zh">🇨🇳 Chinese</option>
                  <option value="ru">🇷🇺 Russian</option>
                </select>
              </div>
              {subtitleLanguage !== 'auto' && (
                <p style={{color:'#64748b',fontSize:'11px',textAlign:'center',margin:'8px 0 0 0'}}>
                  ⚠️ Only change if auto-detect picks the wrong language
                </p>
              )}
            </div>
          )}
          {captionsEnabled && (
            <div style={{margin:'0 0 16px 0',padding:'12px 16px',background: translateLanguage ? 'rgba(6,182,212,0.12)' : 'rgba(124,58,237,0.08)',borderRadius:'12px',border: translateLanguage ? '1px solid rgba(6,182,212,0.4)' : '1px solid rgba(124,58,237,0.2)',opacity:(isLoading||isSubmitting)?0.5:1,pointerEvents:(isLoading||isSubmitting)?'none':'auto'}}>
              <div style={{display:'flex',alignItems:'center',gap:'10px',flexWrap:'wrap',justifyContent:'center'}}>
                <span style={{fontSize:'16px'}}>🌐</span>
                <span style={{color:'#67e8f9',fontSize:'13px',fontWeight:'700'}}>Translate Captions</span>
                <select
                  value={translateLanguage}
                  onChange={(e) => {
                    if (e.target.value === '_locked') {
                      setUpgradeModal({ open: true, plan: 'pro', feature: 'All 30 Translation Languages' });
                      return;
                    }
                    setTranslateLanguage(e.target.value);
                  }}
                  style={{
                    background:'rgba(30,41,59,0.9)',
                    color:'#f1f5f9',
                    border:'1px solid rgba(6,182,212,0.4)',
                    borderRadius:'8px',
                    padding:'6px 12px',
                    fontSize:'13px',
                    fontWeight:'600',
                    cursor:'pointer',
                    outline:'none',
                    flex:'1',
                    minWidth:'140px',
                    maxWidth:'200px'
                  }}
                >
                  <option value="">None (Original)</option>
                  <option value="en">🇬🇧 English</option>
                  <option value="es">🇪🇸 Spanish</option>
                  {(user.plan === 'pro' || user.plan === 'business') ? (<>
                  <option value="fr">🇫🇷 French</option>
                  <option value="de">🇩🇪 German</option>
                  <option value="pt">🇵🇹 Portuguese</option>
                  <option value="it">🇮🇹 Italian</option>
                  <option value="nl">🇳🇱 Dutch</option>
                  <option value="ar">🇸🇦 Arabic</option>
                  <option value="hi">🇮🇳 Hindi</option>
                  <option value="ja">🇯🇵 Japanese</option>
                  <option value="ko">🇰🇷 Korean</option>
                  <option value="zh">🇨🇳 Chinese</option>
                  <option value="ru">🇷🇺 Russian</option>
                  <option value="tr">🇹🇷 Turkish</option>
                  <option value="pl">🇵🇱 Polish</option>
                  <option value="sv">🇸🇪 Swedish</option>
                  <option value="no">🇳🇴 Norwegian</option>
                  <option value="da">🇩🇰 Danish</option>
                  <option value="fi">🇫🇮 Finnish</option>
                  <option value="el">🇬🇷 Greek</option>
                  <option value="cs">🇨🇿 Czech</option>
                  <option value="ro">🇷🇴 Romanian</option>
                  <option value="hu">🇭🇺 Hungarian</option>
                  <option value="th">🇹🇭 Thai</option>
                  <option value="vi">🇻🇳 Vietnamese</option>
                  <option value="id">🇮🇩 Indonesian</option>
                  <option value="ms">🇲🇾 Malay</option>
                  <option value="uk">🇺🇦 Ukrainian</option>
                  <option value="he">🇮🇱 Hebrew</option>
                  <option value="bn">🇧🇩 Bengali</option>
                  <option value="lt">🇱🇹 Lithuanian</option>
                  </>) : (
                  <option value="_locked">🔒 29 more languages (Pro)</option>
                  )}
                </select>
              </div>
              {translateLanguage && (
                <p style={{color:'#67e8f9',fontSize:'11px',textAlign:'center',margin:'8px 0 0 0',fontWeight:'600'}}>
                  Captions will be translated{(user.plan === 'pro' || user.plan === 'business') ? ' — 30 languages supported' : ' — upgrade to Pro for 30 languages'}
                </p>
              )}
            </div>
          )}
          {captionsEnabled && (
            <div style={{margin:'0 0 16px 0',padding:'12px 16px',background:'rgba(124,58,237,0.08)',borderRadius:'12px',border:'1px solid rgba(124,58,237,0.2)',opacity:(isLoading||isSubmitting)?0.5:1,pointerEvents:(isLoading||isSubmitting)?'none':'auto'}}>
              <div style={{display:'flex',alignItems:'center',gap:'10px',flexWrap:'wrap',justifyContent:'center'}}>
                <span style={{fontSize:'16px'}}>✨</span>
                <span style={{color:'#c4b5fd',fontSize:'13px',fontWeight:'700'}}>Caption Style</span>
                <CaptionStylePicker
                  value={captionStyle}
                  onChange={setCaptionStyle}
                  userPlan={user.plan}
                  onLockedClick={(name) => setUpgradeModal({ open: true, plan: 'pro', feature: name })}
                />
              </div>
            </div>
          )}
          {captionsEnabled && (user.plan === 'pro' || user.plan === 'business') && (
            <div style={{margin:'0 0 16px 0',padding:'12px 16px',background:'rgba(124,58,237,0.08)',borderRadius:'12px',border:'1px solid rgba(124,58,237,0.2)',opacity:(isLoading||isSubmitting)?0.5:1,pointerEvents:(isLoading||isSubmitting)?'none':'auto'}}>
              <div style={{display:'flex',alignItems:'center',gap:'10px',flexWrap:'wrap',justifyContent:'center'}}>
                <span style={{fontSize:'16px'}}>🎨</span>
                <span style={{color:'#c4b5fd',fontSize:'13px',fontWeight:'700'}}>Highlight Colour</span>
                <div style={{display:'flex',gap:'8px',flexWrap:'wrap'}}>
                  {[
                    {name:'yellow',hex:'#FFFF00'},
                    {name:'green',hex:'#00FF00'},
                    {name:'cyan',hex:'#00FFFF'},
                    {name:'pink',hex:'#FF60CB'},
                    {name:'red',hex:'#FF0000'},
                    {name:'orange',hex:'#FF8000'}
                  ].map(c => (
                    <div
                      key={c.name}
                      onClick={() => setCaptionColour(c.name)}
                      style={{
                        width:'28px',height:'28px',borderRadius:'50%',
                        backgroundColor:c.hex,cursor:'pointer',
                        border: captionColour === c.name ? '3px solid white' : '2px solid rgba(255,255,255,0.2)',
                        boxShadow: captionColour === c.name ? '0 0 12px ' + c.hex : 'none',
                        transition:'all 0.2s ease'
                      }}
                      title={c.name}
                    />
                  ))}
                </div>
              </div>
            </div>
          )}
          <div style={{margin:'0 0 16px 0',padding:'12px 16px',background:'rgba(124,58,237,0.08)',borderRadius:'12px',border:'1px solid rgba(124,58,237,0.2)',opacity:(isLoading||isSubmitting)?0.5:1,pointerEvents:(isLoading||isSubmitting)?'none':'auto'}}>
            <div style={{display:'flex',alignItems:'center',gap:'10px',flexWrap:'wrap',justifyContent:'center'}}>
              <span style={{fontSize:'16px'}}>😍</span>
              <span style={{color:'#c4b5fd',fontSize:'13px',fontWeight:'700'}}>Inline Emoji</span>
              <div style={{display:'flex',gap:'6px',flexWrap:'wrap'}}>
                {['auto','','🔥','😂','💯','😍','🎯','💪','🤯','👀','❤️','⚡'].map(e => (
                  <div
                    key={e || 'off'}
                    onClick={() => {
                      if (e && e !== '' && user.plan !== 'pro' && user.plan !== 'business') {
                        setUpgradeModal({ open: true, plan: 'pro', feature: 'Inline Emoji Captions' });
                        return;
                      }
                      setEmojiOverlay(e);
                    }}
                    style={{
                      width: e === 'auto' ? 'auto' : '34px', height:'34px',borderRadius:'8px',
                      display:'flex',alignItems:'center',justifyContent:'center',
                      fontSize: (e && e !== 'auto') ? '18px' : '11px',
                      padding: e === 'auto' ? '0 10px' : '0',
                      background: emojiOverlay === e ? 'rgba(124,58,237,0.4)' : 'rgba(30,41,59,0.6)',
                      border: emojiOverlay === e ? '2px solid #a78bfa' : '1px solid rgba(255,255,255,0.1)',
                      cursor:'pointer',transition:'all 0.2s ease',
                      boxShadow: emojiOverlay === e ? '0 0 10px rgba(124,58,237,0.5)' : 'none'
                    }}
                  >{e === 'auto' ? <span style={{color:'#34d399',fontWeight:'700'}}>Auto</span> : e || <span style={{color:'#64748b'}}>Off</span>}</div>
                ))}
              </div>
            </div>
          </div>
          <div className="button-group">
            <button
              onClick={handleGenerate}
              disabled={isLoading || isSubmitting || (pendingJobId && pendingJobStatus !== 'completed')}
              className={`generate-button ${isLoading ? 'loading' : ''}`}
            >
              <span className="button-icon">✨</span>
              <span className="button-text">
                {isLoading ? 'Creating Magic...' : isSubmitting ? 'Submitting...' : clips.length > 0 ? 'Completed ✓ — Click for New Video' : 'Generate Viral Clips'}
              </span>
              <div className="button-shimmer"></div>
            </button>
            
            {isLoading && (
              <button 
                onClick={handleCancel} 
                className="cancel-button"
                title="Cancel current processing"
                style={{
                  background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.9) 0%, rgba(220, 38, 38, 0.9) 100%)',
                  border: '1px solid rgba(239, 68, 68, 0.3)',
                  borderRadius: '12px',
                  color: '#ffffff',
                  padding: '12px 20px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  backdropFilter: 'blur(10px)',
                  boxShadow: '0 4px 20px rgba(239, 68, 68, 0.3)',
                  transition: 'all 0.3s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
                onMouseOver={(e) => {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 8px 30px rgba(239, 68, 68, 0.4)';
                }}
                onMouseOut={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 4px 20px rgba(239, 68, 68, 0.3)';
                }}
              >
                <span className="cancel-icon" style={{
                  fontSize: '16px',
                  filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))'
                }}>⚡</span>
                <span className="cancel-text">Cancel Processing</span>
              </button>
            )}
            
            {!isLoading && (
              <button 
                onClick={handleRefresh} 
                disabled={isLoading}
                className="refresh-button"
                title="Start fresh with a new YouTube URL"
              >
                <span className="refresh-icon">🔄</span>
                <span className="refresh-text">New Video</span>
              </button>
            )}
          </div>
        </div>
        
        {/* Custom Duration for Business Plan */}
        {user.plan === 'business' && (
          <div className="duration-section">
            <div className="duration-header">
              <span className="duration-icon">⏱️</span>
              <h4>Custom Clip Duration (Business Feature)</h4>
            </div>
            <div className="duration-control">
              <label htmlFor="clipDuration">Clip Length:</label>
              <input
                type="range"
                id="clipDuration"
                min="15"
                max="60"
                step="5"
                value={customDuration}
                onChange={(e) => setCustomDuration(e.target.value)}
                className="duration-slider"
              />
              <span className="duration-value">{customDuration} seconds</span>
            </div>
            <div className="duration-options">
              <button 
                className={`duration-preset ${customDuration === 15 ? 'active' : ''}`}
                onClick={() => setCustomDuration(15)}
              >
                15s - Quick
              </button>
              <button 
                className={`duration-preset ${customDuration === 20 ? 'active' : ''}`}
                onClick={() => setCustomDuration(20)}
              >
                20s - Medium
              </button>
              <button 
                className={`duration-preset ${customDuration === 30 ? 'active' : ''}`}
                onClick={() => setCustomDuration(30)}
              >
                30s - Standard
              </button>
              <button 
                className={`duration-preset ${customDuration === 60 ? 'active' : ''}`}
                onClick={() => setCustomDuration(60)}
              >
                60s - Extended
              </button>
            </div>
          </div>
        )}

        <div className="features-grid">
          <div className="feature-item">
            <div className="feature-icon">🎯</div>
            <div className="feature-text">AI-Powered Highlights</div>
          </div>
          <div className="feature-item">
            <div className="feature-icon">⚡</div>
            <div className="feature-text">Lightning Fast</div>
          </div>
          <div className="feature-item">
            <div className="feature-icon">📱</div>
            <div className="feature-text">Social Media Ready</div>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div id="clip-error-msg" style={{background:'rgba(239,68,68,0.15)', border:'2px solid #ef4444', borderRadius:'12px', padding:'16px 20px', margin:'16px 0', display:'flex', alignItems:'center', gap:'12px', backdropFilter:'blur(10px)'}}>
          <div style={{fontSize:'24px', flexShrink:0}}>⚠️</div>
          <div style={{color:'#fecaca', fontSize:'14px', fontWeight:'600', lineHeight:'1.5'}}>{error}</div>
        </div>
      )}

      {/* Progress Section */}

      {progress.status !== 'idle' && progress.status !== 'cancelled' && progress.status !== 'error' && !(progress.status === 'batch_complete' && !isLoading) && (
        <div className="progress-section" id="processing">
          <div className="progress-header">
            <h3 className="progress-title">
              <span className="progress-emoji">⚡</span>
              Creating Your Viral Moment
            </h3>
            <div className="progress-percentage">{progress.progress}%</div>
          </div>
          
          <div className="progress-bar-container">
            <div className="progress-bar">
              <div
                className="progress-fill"
                style={{ width: `${progress.progress}%` }}
              >
                <div className="progress-shine"></div>
              </div>
            </div>
          </div>
          
          <div className="progress-status">
            <div className="progress-step" key={'step-' + progress.step}>{progress.step}</div>
            <div className="progress-message" key={'msg-' + progress.message}>{progress.message}</div>
            {isLoading && (
              <div style={{marginTop:'12px',padding:'10px 16px',background:'rgba(16,185,129,0.1)',borderRadius:'10px',border:'1px solid rgba(16,185,129,0.2)',textAlign:'center'}}>
                <div style={{color:'#34d399',fontSize:'13px',fontWeight:'600'}}>
                  ✉️ You can close this tab — we'll email you when your clips are ready!
                </div>
              </div>
            )}
            {funMessage && (<div style={{marginTop:'16px',padding:'16px 20px',background:'linear-gradient(135deg, rgba(124,58,237,0.25) 0%, rgba(59,130,246,0.25) 50%, rgba(236,72,153,0.25) 100%)',backgroundSize:'200% 200%',animation:'gradientPulse 4s ease infinite, fadeInOut 5s ease-in-out infinite',borderRadius:'14px',border:'1px solid rgba(167,139,250,0.3)',boxShadow:'0 4px 20px rgba(124,58,237,0.2)',textAlign:'center',width:'100%'}}><div style={{color:'#f1f5f9',fontSize:'16px',fontWeight:'700',letterSpacing:'0.3px',lineHeight:'1.5'}}>{funMessage}</div></div>)}
          </div>
          
          <div className="progress-animation">
            <div className="pulse-circle pulse-1"></div>
            <div className="pulse-circle pulse-2"></div>
            <div className="pulse-circle pulse-3"></div>
          </div>
        </div>
      )}

      {/* Clips Results — shared component */}
      <ClipResultsSection
        clips={clips}
        plan={user.plan}
        onWatch={(clip, index) => { setSelectedClip({...clip, index}); setModalOpen(true); }}
        onDownload={handleDownload}
        onRefresh={handleRefresh}
        getEngagementExplanation={getEngagementExplanation}
        previousClips={clips.length === 0 ? previousClips : []}
        previousClipsExpiry={previousClipsExpiry}
      />

      {/* Enhanced Video Preview Modal */}
      {modalOpen && selectedClip && (
        <div className="video-modal-overlay" onClick={closeModal}>
          <div className="enhanced-video-modal" onClick={(e) => e.stopPropagation()}>
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'12px 16px',borderBottom:'1px solid rgba(255,255,255,0.06)'}}>
              <button onClick={closeModal} style={{
                display:'flex',alignItems:'center',gap:'6px',
                background:'none',border:'none',padding:'4px 0',
                color:'#a78bfa',fontSize:'13px',fontWeight:'600',cursor:'pointer',
                transition:'color 0.2s'
              }}
                onMouseEnter={e => { e.currentTarget.style.color = '#c4b5fd'; }}
                onMouseLeave={e => { e.currentTarget.style.color = '#a78bfa'; }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5"/><polyline points="12 19 5 12 12 5"/></svg>
                Back to results
              </button>
              <button onClick={closeModal} style={{
                background:'rgba(255,255,255,0.06)',border:'none',borderRadius:'50%',
                width:'28px',height:'28px',display:'flex',alignItems:'center',justifyContent:'center',
                color:'#64748b',fontSize:'16px',cursor:'pointer',transition:'all 0.2s'
              }}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.12)'; e.currentTarget.style.color = '#e2e8f0'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; e.currentTarget.style.color = '#64748b'; }}
              >×</button>
            </div>
            <div className="modal-content-wrapper">

              {/* Left Column - Custom Video Player */}
              <div className="modal-video-column">
                <div className="video-player-section" style={{position:'relative',borderRadius:'16px',overflow:'hidden',background:'#000'}}>
                  <video
                    ref={(el) => { if (el && !el._customInit) { el._customInit = true; el.addEventListener('timeupdate', () => { const bar = document.getElementById('vp-progress'); const time = document.getElementById('vp-time'); if (bar) bar.style.width = (el.currentTime / el.duration * 100) + '%'; if (time) { const c = Math.floor(el.currentTime); const d = Math.floor(el.duration || 0); time.textContent = Math.floor(c/60) + ':' + String(c%60).padStart(2,'0') + ' / ' + Math.floor(d/60) + ':' + String(d%60).padStart(2,'0'); } }); el.addEventListener('play', () => { const btn = document.getElementById('vp-playbtn'); if (btn) btn.textContent = '⏸'; }); el.addEventListener('pause', () => { const btn = document.getElementById('vp-playbtn'); if (btn) btn.textContent = '▶'; }); } }}
                    id="vp-video"
                    style={{width:'100%',display:'block',borderRadius:'16px 16px 0 0'}}
                    autoPlay
                    playsInline
                    onClick={(e) => { e.target.paused ? e.target.play() : e.target.pause(); }}
                  >
                    <source src={`${selectedClip.file}`} type="video/mp4" />
                  </video>

                  {/* Custom Controls Bar */}
                  <div style={{
                    position:'absolute',bottom:0,left:0,right:0,
                    background:'linear-gradient(transparent, rgba(0,0,0,0.85))',
                    padding:'30px 16px 14px',
                    display:'flex',flexDirection:'column',gap:'8px'
                  }}>
                    {/* Progress Bar */}
                    <div
                      style={{width:'100%',height:'5px',background:'rgba(255,255,255,0.2)',borderRadius:'3px',cursor:'pointer',position:'relative'}}
                      onClick={(e) => {
                        const vid = document.getElementById('vp-video');
                        if (vid) { const rect = e.currentTarget.getBoundingClientRect(); vid.currentTime = ((e.clientX - rect.left) / rect.width) * vid.duration; }
                      }}
                    >
                      <div id="vp-progress" style={{height:'100%',background:'linear-gradient(90deg,#7c3aed,#3b82f6)',borderRadius:'3px',width:'0%',transition:'width 0.1s',pointerEvents:'none'}}></div>
                    </div>

                    {/* Controls Row */}
                    <div style={{display:'flex',alignItems:'center',justifyContent:'space-between'}}>
                      <div style={{display:'flex',alignItems:'center',gap:'12px'}}>
                        {/* Play/Pause */}
                        <button id="vp-playbtn" onClick={() => { const v = document.getElementById('vp-video'); v && (v.paused ? v.play() : v.pause()); }}
                          style={{background:'none',border:'none',color:'white',fontSize:'18px',cursor:'pointer',padding:'4px 6px',lineHeight:1}}>⏸</button>

                        {/* Mute/Unmute */}
                        <button onClick={() => { const v = document.getElementById('vp-video'); if (v) { v.muted = !v.muted; document.getElementById('vp-mutebtn').textContent = v.muted ? '🔇' : '🔊'; } }}
                          id="vp-mutebtn"
                          style={{background:'none',border:'none',color:'white',fontSize:'16px',cursor:'pointer',padding:'4px 6px',lineHeight:1}}>🔊</button>

                        {/* Time */}
                        <span id="vp-time" style={{color:'rgba(255,255,255,0.7)',fontSize:'12px',fontWeight:'600',fontFamily:'monospace'}}>0:00 / 0:00</span>
                      </div>

                      <div style={{display:'flex',alignItems:'center',gap:'10px'}}>
                        {/* Download */}
                        <button onClick={() => downloadVideo(selectedClip.file, 'vlogclip_' + (selectedClip.index + 1) + '.mp4')}
                          style={{background:'none',border:'none',color:'white',fontSize:'16px',cursor:'pointer',padding:'4px 6px',lineHeight:1}} title="Download">💾</button>

                        {/* Fullscreen */}
                        <button onClick={() => { const v = document.getElementById('vp-video'); if (v) { if (v.requestFullscreen) v.requestFullscreen(); else if (v.webkitRequestFullscreen) v.webkitRequestFullscreen(); } }}
                          style={{background:'none',border:'none',color:'white',fontSize:'16px',cursor:'pointer',padding:'4px 6px',lineHeight:1}} title="Fullscreen">⛶</button>
                      </div>
                    </div>
                  </div>

                  {/* Timestamp Badge */}
                  <div style={{position:'absolute',top:'12px',left:'12px',background:'rgba(0,0,0,0.6)',backdropFilter:'blur(8px)',padding:'5px 12px',borderRadius:'8px',fontSize:'11px',color:'rgba(255,255,255,0.8)',fontWeight:'600'}}>
                    {selectedClip.timestamp}
                  </div>
                </div>
              </div>

              {/* Right Column - Engagement Details */}
              <div className="modal-info-column">
                <button className="modal-close-btn" onClick={closeModal}>✕</button>
                
                {/* Engagement Level */}
                <div className="engagement-level-section">
                  <div className="engagement-percentage">
                    <span className="engagement-label">Engagement Level:</span>
                    <span className="engagement-value">{Math.round((selectedClip.engagement_score||selectedClip.engagementScore/100||0.85) * 100)}% High</span>
                  </div>
                </div>

                {/* Why This Score Section */}
                <div className="why-score-section">
                  <h3 className="section-title">Why This Score:</h3>
                  <div className="score-explanation">
                    <div className="explanation-summary">{getEngagementExplanation(selectedClip).summary}</div>
                    <div className="explanation-details">{getEngagementExplanation(selectedClip).details}</div>
                    <div className="explanation-metrics">{getEngagementExplanation(selectedClip).metrics}</div>
                  </div>
                </div>

                {/* Viral Potential */}
                <div className="viral-potential-section">
                  <div className="viral-row">
                    <span className="viral-label">Viral Potential:</span>
                    <span className="viral-value">Excellent</span>
                  </div>
                </div>

                {/* Best Platform */}
                <div className="best-platform-section">
                  <div className="platform-row">
                    <span className="platform-label">Best Platform:</span>
                    <span className="platform-value">TikTok, Instagram Reels</span>
                  </div>
                </div>

                {/* Quick Share */}
                <div className="quick-share-section">
                  <h4 style={{color:'#e2e8f0', fontSize:'14px', marginBottom:'10px', display:'flex', alignItems:'center', gap:'6px'}}>🚀 Quick Share</h4>
                  <div style={{display:'flex', gap:'8px', flexWrap:'wrap'}}>
                    <button onClick={() => { window.open('https://twitter.com/intent/tweet?text=' + encodeURIComponent((selectedClip.captions?.twitter || selectedClip.headline) + '\n\nMade with VlogClip AI 🎬'), '_blank'); }} style={{padding:'8px 14px', background:'#1DA1F2', border:'none', borderRadius:'8px', color:'white', fontSize:'12px', fontWeight:'700', cursor:'pointer'}}>🐦 Twitter</button>
                    <button onClick={() => { navigator.clipboard.writeText((selectedClip.captions?.instagram || selectedClip.headline) + '\n\nMade with VlogClip AI 🎬'); alert('Caption copied! Opening Facebook - paste your caption and upload your downloaded clip.'); window.open('https://www.facebook.com/', '_blank'); }} style={{padding:'8px 14px', background:'#4267B2', border:'none', borderRadius:'8px', color:'white', fontSize:'12px', fontWeight:'700', cursor:'pointer'}}>📘 Facebook</button>
                    <button onClick={() => { navigator.clipboard.writeText((selectedClip.captions?.linkedin || selectedClip.headline) + '\n\nMade with VlogClip AI 🎬'); alert('Caption copied! Opening LinkedIn - paste your caption and upload your downloaded clip.'); window.open('https://www.linkedin.com/feed/', '_blank'); }} style={{padding:'8px 14px', background:'#0077B5', border:'none', borderRadius:'8px', color:'white', fontSize:'12px', fontWeight:'700', cursor:'pointer'}}>💼 LinkedIn</button>
                    <button onClick={() => { const videoUrl = window.location.origin + (selectedClip.file || selectedClip.videoUrl); navigator.clipboard.writeText(videoUrl); alert('Video link copied! Download the clip first, then upload to your preferred platform.'); }} style={{padding:'8px 14px', background:'#6366f1', border:'none', borderRadius:'8px', color:'white', fontSize:'12px', fontWeight:'700', cursor:'pointer'}}>🔗 Copy Link</button>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="modal-action-buttons">
                  <button 
                    className="download-video-btn"
                    onClick={() => downloadVideo(selectedClip.file, 'vlogclip_' + (selectedClip.index + 1) + '.mp4')}
                  >
                    Download Video
                  </button>
                  <button className="back-to-results-btn" onClick={closeModal}>
                    ← Back to Results
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      <UpgradeModal
        open={upgradeModal.open}
        onClose={() => setUpgradeModal({ ...upgradeModal, open: false })}
        requiredPlan={upgradeModal.plan}
        featureName={upgradeModal.feature}
        currentLimit={upgradeModal.currentLimit}
      />

      {/* Floating button is now built into ClipResultsSection */}
    </div>
  );
};

export default ClipGenerator;
